import { Form, Modal } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { hideModal } from '../../../store/actions/modalActions'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons/faFileInvoice'
import React, { ChangeEvent, useState } from 'react'
import { selectModalData } from '../../../store/selectors/modalSelectors'
import { selectProductsConfigs } from '../../../store/selectors/configSelectors'
import { downloadProductConfig } from '../../../store/async-thunks/products/downloadProductConfig'
import Loader from '../../Loader/Loader'
import { throwSuccessMessage } from '../../../utils/Errors'

export const ConfigsModal = () => {
    const dispatch = useAppDispatch()

    const { guid } = useAppSelector(selectModalData) ?? {}

    const productsConfigurations = useAppSelector(selectProductsConfigs)
    const productSchemaAndConfig = productsConfigurations[guid]
    const configuration = productSchemaAndConfig.configuration
    const configNames = Object.keys(configuration)
    const [touchedConfigs, setTouchedConfigs] = useState<
        Record<string, boolean>
    >({})
    const [preventDownload, setPreventDownload] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const touchedConfigsEntries = Object.entries(touchedConfigs)
    const pickedConfigsEntries = touchedConfigsEntries.filter(
        ([_, isChecked]) => isChecked
    )
    const isDownloadDisabled = preventDownload || !pickedConfigsEntries.length

    const handleClose = () => dispatch(hideModal())

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name
        const isChecked = e.target.checked
        setTouchedConfigs((prev: typeof touchedConfigs) => ({
            ...prev,
            [name]: isChecked,
        }))
    }

    const downloadConfigs = async () =>
        await Promise.all(
            pickedConfigsEntries.map(([configName]) =>
                dispatch(
                    downloadProductConfig({
                        configName,
                        guid,
                    })
                )
            )
        )

    const handleDownload = async () => {
        setPreventDownload(true)
        setIsLoading(true)
        try {
            const result = await downloadConfigs()
            if (
                result.every(
                    (res) =>
                        res.meta.requestStatus === 'fulfilled' ||
                        res.meta.requestStatus === 'rejected'
                )
            ) {
                setIsLoading(false)
                setPreventDownload(false)
            }
            if (result.every((a) => a.meta.requestStatus === 'fulfilled')) {
                throwSuccessMessage('Successfully downloaded')
                setTimeout(() => handleClose(), 0)
            }
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <>
            <div className="bg-blur" />
            <Modal
                centered
                show={true}
                onHide={handleClose}
            >
                <Modal.Header
                    className="px-4 py-3"
                    closeButton
                >
                    <Modal.Title>
                        <FormattedMessage id="modals.configs.downloadTitle" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">
                    {isLoading ? (
                        <div className="text-center">
                            <Loader />
                        </div>
                    ) : (
                        <Form className="importantName">
                            {configNames.map((configName) => (
                                <Form.Check
                                    type="checkbox"
                                    className="mb-2"
                                    key={configName}
                                    id={configName}
                                    name={configName}
                                    value={configName}
                                    checked={touchedConfigs[configName]}
                                    onChange={handleChange}
                                    label={
                                        <>
                                            <span className="mx-1">
                                                {configName}
                                            </span>
                                            <FontAwesomeIcon
                                                icon={faFileInvoice}
                                            />
                                        </>
                                    }
                                />
                            ))}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        disabled={isDownloadDisabled}
                        className="app-btn-main app-btn-apply"
                        onClick={handleDownload}
                    >
                        Download
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
